// WAAP business/billing usage — private-portal reads (account-wide, per month).
//
// NOTE: kept on PRIVATE — the public GET /business-usage surface is
// currently broken by an SDK bug (conversant@v0.44.0): it returns 401
// "Signature.NotMatch" (the same HMAC signing bug that affects the SSL endpoints).
// billing-usage has no public route at all. Re-migrate business-usage once the
// SDK signing is fixed.
//
// These hit the PRIVATE (SSO/portal) backend reads, behind
//   GET /waap/domains/:id/business-usage?month=YYYY-MM   (per-domain rows)
//   GET /waap/domains/:id/billing-usage?month=YYYY-MM    (account summary)
// The `:id` only resolves the customer session server-side — the data is
// account-wide. The backend passes the raw vendor payload through, which is the
// `{ data, msg, code }` envelope, so the hooks unwrap `.data`.
//
// NOTE: the backend routes + the conversant SDK methods (GetBusinessUsage /
// GetBillingUsage) are not in the released SDK yet — until they land these
// reads 500/404 and the page shows its error/empty state. See usage.ts hooks
// kept stable so the tab lights up once the backend is wired.
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** One per-domain business-usage row (raw vendor shape). */
export interface WaapBusinessUsageRow {
  customerId?: number;
  domain?: string;
  time?: string;
  /** Total volume in bytes. */
  volume?: number;
  /** Peak bandwidth in bits/sec. */
  bandwidth?: number;
  /** Peak requests/sec. */
  rps?: number;
}

/** Account-level billing-usage summary (raw vendor shape). */
export interface WaapBillingUsage {
  /** Aggregate volume in bytes. */
  volume?: number;
  /** Peak requests/sec across all domains. */
  rps?: number;
  /** Peak bandwidth in bits/sec across all domains. */
  bandwidth?: number;
  root_domains?: number;
  domains?: number;
}

/** Unwrap the vendor `{ data }` envelope (or pass a bare value through). */
function unwrap<T>(raw: unknown): T | null {
  if (raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)) {
    return ((raw as Record<string, unknown>).data ?? null) as T | null;
  }
  return (raw ?? null) as T | null;
}

const usagePath = (domainId: string, area: string, month: string) =>
  `/waap/domains/${domainId}/${area}?month=${encodeURIComponent(month)}`;

/** Per-domain business usage for the given month (YYYY-MM). */
export function useWaapBusinessUsage(domainId: string | undefined, month: string) {
  return useQuery({
    queryKey: ['waap', 'domains', domainId ?? '', 'business-usage', month] as const,
    enabled: !!domainId && !!month,
    retry: false,
    queryFn: async (): Promise<WaapBusinessUsageRow[]> => {
      const res = await serviceApiV3.get(usagePath(domainId as string, 'business-usage', month));
      const data = unwrap<WaapBusinessUsageRow[]>(res.data);
      return Array.isArray(data) ? data : [];
    },
  });
}

/** Account-level billing usage summary for the given month (YYYY-MM). */
export function useWaapBillingUsage(domainId: string | undefined, month: string) {
  return useQuery({
    queryKey: ['waap', 'domains', domainId ?? '', 'billing-usage', month] as const,
    enabled: !!domainId && !!month,
    retry: false,
    queryFn: async (): Promise<WaapBillingUsage | null> => {
      const res = await serviceApiV3.get(usagePath(domainId as string, 'billing-usage', month));
      return unwrap<WaapBillingUsage>(res.data);
    },
  });
}
